import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Badge, Box, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import useAuth from "src/hooks/useAuth";
import SupportChat from "./SupportChat";
import { fetchConversationsList, isSupportConfigured } from "src/utils/supportClient";

/**
 * SupportFab — la porta di Jinkō sul pannello Network (stadio 2, brief 16/08).
 * Gemello di app/src/components/Support/SupportFab.js: stessa bolla
 * (gradient viola-oro, 56px, pulse), montata in layouts/layout.jsx così copre
 * area utente E area admin in entrambe le varianti di layout. /hub resta
 * senza bolla (decisione 16/08: schermata di transito).
 *
 * Render solo per utenti AUTENTICATI: il servizio rifiuta comunque senza
 * token, ma mostrare la bolla a un guest produrrebbe solo un giro di 401.
 *
 * BETA CHIUSA: se REACT_APP_SUPPORT_BETA_USERNAMES è valorizzata (lista di
 * username separati da virgola, inlined al build), la bolla appare SOLO a
 * loro. È solo cosmesi anti porta-morta: il cancello vero è l'allowlist
 * SUPPORT_BETA_USER_IDS lato servizio, che rifiuta con 403 chiunque altro.
 */
const BETA_USERNAMES = (process.env.REACT_APP_SUPPORT_BETA_USERNAMES || "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean);

export default function SupportFab() {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // Pallino sulla bolla: c'è un filo che ti aspetta (arrivo dall'altra area
  // o accompagnamento in corso). La chat NON si apre più da sola — decisione
  // 16/08: all'arrivo deve vedersi la PAGINA, la bolla segnala.
  const [pending, setPending] = useState(false);
  // Cambia a ogni adozione di un filo dall'altra area: SupportChat lo usa per
  // rileggere il flag di arrivo (i suoi effetti, da figlio, girano PRIMA di
  // questi — senza un segnale esplicito non vedrebbe mai il flag appena scritto).
  const [arrivalNonce, setArrivalNonce] = useState(0);

  // PONTE DEL FILO: arrivo dall'altra area con la conversazione nel link
  // (?support_conv=<uuid>, scritto dal widget di partenza). Si adotta il filo
  // del server (stesso userId, il server ignora id altrui) e si accende il
  // pallino. Il param si toglie SUBITO dall'URL: un refresh non ri-adotta.
  useEffect(() => {
    if (!user) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const conv = params.get("support_conv");
      if (!conv) return;
      params.delete("support_conv");
      const rest = params.toString();
      window.history.replaceState(
        null,
        "",
        window.location.pathname + (rest ? `?${rest}` : "") + window.location.hash
      );
      if (!/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(conv)) return;
      if (sessionStorage.getItem("support_conversation_id") !== conv) {
        // La cronologia arriverà dal server all'apertura (fetch del widget):
        // il transcript locale dell'altra conversazione si azzera.
        sessionStorage.removeItem("support_transcript");
        sessionStorage.setItem("support_conversation_id", conv);
        sessionStorage.setItem("support_resumed", "1");
      }
      // ACCOMPAGNAMENTO cross-area (3-C): la sessionStorage non attraversa i
      // domini, quindi il flag guida non arriva fin qui — ma la guida vive
      // LATO SERVER sulla conversazione appena adottata. Si arma il flag di
      // arrivo sulla pagina corrente e a mandarlo e' SupportChat: e' lui che
      // possiede il transcript, quindi la risposta automatica si vede appena
      // arriva (round 10: prima l'evento partiva da qui e la risposta veniva
      // scartata — riaprendo la chat 10s dopo il tap non c'era ancora nulla,
      // e quel pannello non si aggiornava piu'). Il server decide comunque:
      // senza guida attiva risponde {skipped:true}, a costo zero.
      sessionStorage.setItem(
        "support_guide_pending",
        JSON.stringify({ slug: window.location.pathname, at: Date.now() })
      );
      setTimeout(() => {
        setArrivalNonce((n) => n + 1);
        setPending(true);
      }, 0);
    } catch {}
  }, [user]);

  // ACCOMPAGNAMENTO: se il bot ti ha mandato su una pagina (flag scritto da
  // SupportChat al tocco del bottone), all'arrivo si accende il pallino; il
  // flag si consuma all'APERTURA, non alla rilevazione. Il FAB resta montato
  // durante navigate() (SPA): il segnale è il cambio di pathname. Scadenza
  // 10 minuti: un flag vecchio non accende niente (e si pulisce).
  useEffect(() => {
    if (!user) return;
    try {
      const raw = sessionStorage.getItem("support_guide_pending");
      if (!raw) return;
      const { at, seen } = JSON.parse(raw);
      if (Date.now() - at < 10 * 60 * 1000) {
        // `seen` = la chat e' gia' stata aperta con questo flag: il pallino
        // resta spento finche' una guida nuova non riscrive il flag.
        if (!seen) setTimeout(() => setPending(true), 0);
      } else {
        sessionStorage.removeItem("support_guide_pending");
        // Flag scaduto: il pallino si spegne (a meno che non lo tenga acceso
        // un attraversamento in attesa) — senza, restava acceso a vuoto.
        if (sessionStorage.getItem("support_resumed") !== "1") {
          setTimeout(() => setPending(false), 0);
        }
      }
    } catch {}
  }, [user, pathname]);

  // PALLINO "risposta dal team" (round 17, punto 2 della coda): polling
  // leggero di /api/conversations — zero costo modello, e il passaggio
  // autenticato CONSEGNA anche le risposte in coda (3-D), quindi il giro
  // "approvata nel gruppo → pallino acceso" si chiude da solo entro un
  // minuto. Si accende se un filo aperto ha l'ultima parola del team più
  // nuova del timbro "vista" scritto da SupportChat quando la mostra.
  // Fermo quando la chat è aperta (ci pensa lei) o la tab non è visibile.
  useEffect(() => {
    if (!isAuthenticated || !user || open || !isSupportConfigured()) return;
    let alive = true;
    const controlla = async () => {
      if (!alive || document.visibilityState !== "visible") return;
      try {
        const { conversations } = await fetchConversationsList();
        if (!alive) return;
        // Il "gia' visto" arriva dal SERVER (reply_seen, 20/08): in
        // sessionStorage non attraversava app <-> pannello, e il pallino si
        // riaccendeva su risposte gia' lette da un'altra parte.
        const nuova = (conversations || []).some(
          (c) => c.status === "open" && c.last_role === "human" && !c.reply_seen
        );
        if (nuova) setPending(true);
      } catch {}
    };
    controlla();
    const timer = setInterval(controlla, 60_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") controlla();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isAuthenticated, user, open]);

  const openChat = () => {
    // Il flag NON si cancella (round 10): cancellandolo, chi tocca la bolla
    // SUBITO dopo essere atterrato annullava l'arrivo prima che SupportChat lo
    // consumasse — niente turno auto, niente risposta, per sempre. Qui si
    // segna solo "visto", che spegne il pallino; l'arrivo resta di SupportChat
    // (che marca `fired`) e il flag scade da solo dopo 10 minuti.
    try {
      const raw = sessionStorage.getItem("support_guide_pending");
      if (raw) sessionStorage.setItem("support_guide_pending", JSON.stringify({ ...JSON.parse(raw), seen: true }));
    } catch {}
    setPending(false);
    setOpen(true);
  };

  if (!isAuthenticated || !user || !isSupportConfigured()) return null;
  if (BETA_USERNAMES.length && !BETA_USERNAMES.includes(user.username)) return null;

  // Niente BottomNav nel pannello (a differenza dell'app): la bolla sta
  // vicino al bordo basso su ogni viewport.
  return (
    <>
      {!open && (
        <Tooltip title={t("support.fabTooltip")} arrow enterTouchDelay={0} leaveTouchDelay={2500}>
          <IconButton
            onClick={openChat}
            aria-label={t("support.fabAria")}
            sx={{
              position: "fixed",
              zIndex: (th) => th.zIndex.fab,
              bottom: { xs: 16, md: 24 },
              right: { xs: 16, md: 24 },
              width: 56,
              height: 56,
              background: "linear-gradient(135deg, #a855f7 0%, #facc15 100%)",
              boxShadow: "0 8px 24px rgba(168, 85, 247, 0.45), 0 2px 8px rgba(0,0,0,0.4)",
              transition: "transform .2s ease, box-shadow .2s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #a855f7 0%, #facc15 100%)",
                transform: "translateY(-2px) scale(1.04)",
                boxShadow: "0 12px 32px rgba(168, 85, 247, 0.6), 0 4px 12px rgba(0,0,0,0.5)",
              },
              "&:active": { transform: "translateY(0) scale(0.98)" },
              animation: "jinko-pulse 5s ease-in-out infinite",
              "@keyframes jinko-pulse": {
                "0%, 100%": {
                  boxShadow: "0 8px 24px rgba(168, 85, 247, 0.45), 0 2px 8px rgba(0,0,0,0.4)",
                },
                "50%": {
                  boxShadow:
                    "0 8px 24px rgba(168, 85, 247, 0.85), 0 2px 8px rgba(0,0,0,0.4), 0 0 0 6px rgba(168, 85, 247, 0.18)",
                },
              },
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          >
            <Badge
              color="error"
              variant="dot"
              invisible={!pending}
              overlap="circular"
              sx={{ "& .MuiBadge-dot": { width: 12, height: 12, borderRadius: "50%", border: "2px solid #fff" } }}
            >
              {/* Il volto dentro l'anello sfumato: la bolla resta riconoscibile
                  (stessa posizione, stesso gradiente, stesso pulse). */}
              <Box
                component="img"
                src="/jinko/avatar.webp"
                alt={t("support.fabAria")}
                sx={{ width: 48, height: 48, borderRadius: "50%", display: "block", objectFit: "cover" }}
              />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      <SupportChat open={open} onClose={() => setOpen(false)} arrivalNonce={arrivalNonce} />
    </>
  );
}
